import { computed, ref } from 'vue'
import { createInjectionState, toReactive } from '@vueuse/core'
import { ToQueryStringFilters, useDate, useToQueryString } from '@/core'
import { useModuleQuery } from '../query/useModuleQuery'
import { useI18n } from 'vue-i18n'
import { TDataset } from '../../..'

const [useProvideModule, useModule] = createInjectionState(() => {
  const { t } = useI18n()

  const filters = ref<ToQueryStringFilters>({
    statistic_date: '',
    user_id: [],
  })

  const { zonedTimeToUtc } = useDate()
  const moduleQueryParams = useToQueryString(toReactive(filters))

  const moduleQuery = useModuleQuery({
    filters: {
      loadInvitationStatistics: moduleQueryParams.queryParams,
    },
    enableQuery: {
      loadInvitationStatistics: computed(
        () => !!moduleQueryParams.params.value.length,
      ),
    },
  })

  const onSetInitialDate = () => {
    const date = zonedTimeToUtc(new Date(), {
      removeTimeZone: true,
    })

    filters.value.statistic_date = date
  }

  const loaders = computed(() => ({
    isInvitationsStatisticsLoading:
      moduleQuery.loadInvitationStatistics.isLoading.value,
    isSellersLoading: moduleQuery.sellersCatalog.isLoading.value,
  }))

  const statistics = computed(
    () => moduleQuery.loadInvitationStatistics.data.value,
  )

  const invitationsDoughnutGraphic = computed(() => {
    const data = moduleQuery.loadInvitationStatistics.data.value

    return {
      options: {
        responsive: false,
        plugins: {
          legend: {
            position: 'right',
          },
        },
      },
      data: {
        labels: [
          t('dashboard.ON_TIME'),
          t('dashboard.NOT_SENT'),
          t('dashboard.WRONG_TIME'),
        ],
        datasets: [
          {
            data: [data?.sent_on_time, data?.not_sent, data?.sent_wrong_time],
            backgroundColor: [
              'rgba(234, 98, 90, 1)',
              'rgba(204, 19, 8, 1)',
              'rgba(108, 6, 0, 1)',
            ],
          },
        ],
      },
    }
  })

  const invitationsBarGraphic = computed(() => {
    const data = moduleQuery.loadInvitationStatistics.data.value
    const labels: string[] = []
    const datasets: Record<any, TDataset> = {
      on_time: {
        type: 'bar',
        label: t('dashboard.ON_TIME'),
        backgroundColor: 'rgba(234, 98, 90, 1)',
        data: [],
        borderRadius: 30,
      },
      not_sent: {
        type: 'bar',
        label: t('dashboard.NOT_SENT'),
        backgroundColor: 'rgba(204, 19, 8, 1)',
        data: [],
        borderRadius: 30,
      },
      wrong_time: {
        type: 'bar',
        label: t('dashboard.WRONG_TIME'),
        backgroundColor: 'rgba(108, 6, 0, 1)',
        data: [],
        borderRadius: 30,
      },
    }

    data?.top_leads.forEach((lead) => {
      labels.push(lead.name)
      datasets?.on_time?.data?.push(lead.sent_on_time_count as never)
      datasets?.not_sent?.data?.push(lead.not_sent_count as never)
      datasets?.wrong_time?.data?.push(lead.sent_wrong_time_count as never)
    })

    return {
      options: {
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true,
          },
        },
      },
      data: {
        labels,
        datasets: Object.values(datasets),
      },
    }
  })
  const sellers = computed(() => moduleQuery.sellersCatalog.data.value)

  return {
    sellers,
    loaders,
    statistics,
    filters,
    invitationsDoughnutGraphic,
    invitationsBarGraphic,
    onSetInitialDate,
  }
})

export { useProvideModule, useModule }
